import React, { useState, useCallback, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import useAxios from '../../../services/AxiosConfig';
import { useSelector } from 'react-redux';
import ECGChart from '../../../components/ECGChart';
import { formatDateWithTime } from '../../../utils/dateUtils';

const ChartDetailView = ({ isModalVisible, handleCloseModal, modalData }) => {
  const axiosConfig = useSelector((state) => state.axiosConfig);
  const httpService = useAxios(axiosConfig.envURL);
  const [chartData, setChartData] = useState(null);
  const [vital, setVital] = useState('');
  const [showChart, setShowChart] = useState(false);
  const [showAudio, setShowAudio] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const userDateFormat = useSelector((state) => state.userInfo.dateFormat);


  const normalizedModalityType = (modalData?.modalityType ?? '')
    ?.toString()
    .trim()
    .toLowerCase();
  const isRawDataSupported = normalizedModalityType === 'ecg' || normalizedModalityType === 'ausc';

  const fetchRawReadings = useCallback(async (patientId, completedDate, deviceName, modalityType, vital) => {
    const normalized = (modalityType ?? '')
      ?.toString()
      .trim()
      .toLowerCase();
    // Only these modality types can produce raw readings in this UI.
    if (normalized !== 'ecg' && normalized !== 'ausc' && normalized !== 'audio') {
      setShowNotificationModal(true);
      setShowChart(false);
      setShowAudio(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await httpService.get(`device/readings/raw/${normalized}`, {
        params: {
          completedDate: completedDate,
          accountId: patientId,
        }
      });

      if (response.data.data.fileType === 'csv') {
        const data = response.data.data.fileData;
        const labels = data.map((_, index) => index);
        const values = data.map(value => parseFloat(value));

        setChartData({
          labels,
          datasets: [
            {
              label: deviceName,
              data: values,
              fill: false,
              borderColor: 'rgba(75,192,192,1)',
              tension: 0.1,
              pointRadius: 0
            }
          ]
        });
        setVital(vital);
        setShowChart(true);
        setShowAudio(false);
        
      } else if (response.data.data.fileType === 'wav') {
        const audioData = response.data.data.fileData;
        const audioArray = new Uint8Array(Object.values(audioData));
        const blob = new Blob([audioArray], { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);
        setVital(vital);
        setAudioUrl(url);
        setShowAudio(true);
        setShowChart(false);
      }
    } catch (error) {
      console.error("Failed to fetch raw readings:", error);
    } finally {
      setIsLoading(false);
    }
  }, [httpService]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!isModalVisible) {
      setChartData(null);
      setVital('');
      setShowChart(false);
      setShowAudio(false);
      setAudioUrl('');
    }
  }, [isModalVisible]);

  useEffect(() => {
    if (modalData) {
      // Reset per-modal UI state in case the modal was previously opened for
      // an unsupported modality type.
      setShowNotificationModal(false);
      setShowChart(false);
      setShowAudio(false);
      setChartData(null);
      setAudioUrl('');

      const { timeStamp, deviceName, vital, modalityType, patientId } = modalData;
      fetchRawReadings(patientId, timeStamp, deviceName, modalityType, vital);
    }
  }, [modalData, fetchRawReadings]);

  const modalBodyHeight = window.innerHeight * 0.9;

  const modalStyles = {
    '--bs-modal-zindex': 1055,
    '--bs-modal-padding': '1rem',
    '--bs-modal-margin': '0.5rem',
    '--bs-modal-width': showAudio ? '50%' : '90%', 
    '--bs-modal-height': showAudio ? '30%' : '90%',
  };

  return (
    <>
      {(isRawDataSupported && !showNotificationModal) ? (
        <Modal
          show={isModalVisible}
          onHide={handleCloseModal}
          fullscreen='xl-down'
          centered={true}
          backdrop="static"
          style={modalStyles}
        >
          <Modal.Header className="d-flex justify-content-end align-items-center" style={{ borderBottom: "none" }} closeButton>
            <Modal.Title>
              {vital}
              {!isLoading && 
                <div className="subtitle-2" style={{padding:"10px 0"}}>
                  {formatDateWithTime(modalData?.timeStamp, userDateFormat)}
                </div>
              }
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ height: showAudio ? 'auto' : modalBodyHeight, overflow: 'auto' }}>
            {isLoading ? (
              <div className="loading" />
            ) : (
              <>
                {showChart && chartData && (
                  normalizedModalityType === 'ecg' ? (
                    <div style={{ width: '100%', height: '100%', paddingTop: '20px'}}>
                      <ECGChart
                        data={chartData}
                        posture={modalData?.posture ?? 'Unknown'}
                      />
                    </div>
                  ) : (
                    <Line data={chartData} options={{ maintainAspectRatio: false }} />
                  )
                )}
                {showAudio && audioUrl && (
                  <audio controls style={{ width: '100%' }}>
                    <source src={audioUrl} type="audio/wav" />
                    Your browser does not support the audio element.
                  </audio>
                )}
                {!showChart && !showAudio && !isLoading && (
                  <div>No data available</div>
                )}
              </>
            )}
          </Modal.Body>
        </Modal>
      ) : (
        <Modal
          show={isModalVisible}
          onHide={handleCloseModal}
          size="sm"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Not Available</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Raw data is only available for ECG and Auscultation readings.</p>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default ChartDetailView;
